import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Badge, Popover, List, Button, Typography, Space, Spin, Modal, Pagination, Tag } from 'antd';
import { BellOutlined, CheckOutlined, DeleteOutlined, ReloadOutlined, LinkOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  setNotifications,
  markAsRead,
  markAllAsRead,
  setUnreadCount
} from '../../store/slices/notificationsSlice';
import { notificationsApi } from '../../store/api/notificationsApi';
import { Notification } from '../../types';
import {
  canOpenNotification,
  formatRelativeTimeRu,
  getContextBadges,
  getDisplaySubtitle,
  getDisplayTitle,
  getSemanticTier,
  getTierAccentColor,
  getUnreadItemClassName,
  groupNotificationsForBell,
  GroupedNotification
} from '../../utils/notificationPresentation';
import NotificationSemanticIcon from './NotificationSemanticIcon';
import { isRealtimeClientEnabled, REALTIME_POLL_FALLBACK_MS } from '../../realtime/realtimeConfig';
import { operationalRealtimeClient } from '../../realtime/operationalRealtimeClient';

const { Text } = Typography;

const NotificationBell: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visibleAll, setVisibleAll] = useState(false);
  const [pageAll, setPageAll] = useState(1);
  const [limitAll, setLimitAll] = useState(20);
  const [limitPopover, setLimitPopover] = useState(10);
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'important'>('all');
  const [unreadOnlyFilter, setUnreadOnlyFilter] = useState(false);

  const handleVisibleChange = (newVisible: boolean) => {
    setVisible(newVisible);
    if (newVisible) refetch();
  };

  const [realtimeConnected, setRealtimeConnected] = React.useState(false);
  React.useEffect(() => {
    if (!isRealtimeClientEnabled()) return;
    const id = setInterval(() => setRealtimeConnected(operationalRealtimeClient.isConnected()), 2000);
    return () => clearInterval(id);
  }, []);

  const pollMs = isRealtimeClientEnabled() && realtimeConnected ? 0 : REALTIME_POLL_FALLBACK_MS;

  const {
    data: notificationsData,
    refetch,
    isLoading: isLoadingNotifications,
    isFetching: isFetchingNotifications
  } = notificationsApi.useGetNotificationsQuery(
    { page: 1, limit: limitPopover },
    {
      pollingInterval: pollMs || undefined,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true
    }
  );

  const { data: allData, refetch: refetchAll, isLoading: isLoadingAll } = notificationsApi.useGetNotificationsQuery(
    { page: pageAll, limit: limitAll },
    { skip: !visibleAll, refetchOnMountOrArgChange: true }
  );

  const unreadCount = notificationsData?.unreadCount ?? 0;
  const notifications = useMemo(
    () => notificationsData?.notifications ?? [],
    [notificationsData?.notifications]
  );

  const [markAsReadMutation] = notificationsApi.useMarkAsReadMutation();
  const [markAllAsReadMutation] = notificationsApi.useMarkAllAsReadMutation();
  const [deleteNotificationMutation] = notificationsApi.useDeleteNotificationMutation();

  useEffect(() => {
    if (notificationsData) {
      dispatch(setNotifications(notificationsData.notifications));
      dispatch(setUnreadCount(notificationsData.unreadCount));
    }
  }, [notificationsData, dispatch]);

  const handleNotificationOpen = async (notification: Notification) => {
    try {
      if (!notification.isRead) {
        await markAsReadMutation(notification.id).unwrap();
        dispatch(markAsRead(notification.id));
      }
      const taskId = Number(notification.taskId ?? notification.data?.taskId ?? 0);
      if (Number.isFinite(taskId) && taskId > 0) {
        navigate(`/workspace?taskId=${taskId}`);
        setVisible(false);
        return;
      }
      const actionLink = String(notification.data?.actionLink || '').trim();
      if (actionLink) {
        navigate(actionLink);
        setVisible(false);
      }
    } catch (error) {
      console.error('Ошибка открытия уведомления:', error);
    }
  };

  const handleMarkAsRead = async (notificationId: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await markAsReadMutation(notificationId).unwrap();
      dispatch(markAsRead(notificationId));
    } catch (error) {
      console.error('Ошибка отметки уведомления как прочитанного:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setLoading(true);
      await markAllAsReadMutation().unwrap();
      dispatch(markAllAsRead());
    } catch (error) {
      console.error('Ошибка отметки всех уведомлений как прочитанных:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (notificationId: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await deleteNotificationMutation(notificationId).unwrap();
    } catch (error) {
      console.error('Ошибка удаления уведомления:', error);
    }
  };

  const filterByPriorityAndUnread = useCallback(
    (list: Notification[]) =>
      list.filter((n) => {
        const tier = getSemanticTier(n);
        if (priorityFilter === 'important' && !(tier === 'CRITICAL' || tier === 'WARNING' || tier === 'ACTION_REQUIRED')) {
          return false;
        }
        if (unreadOnlyFilter && n.isRead) return false;
        return true;
      }),
    [priorityFilter, unreadOnlyFilter]
  );

  const filteredNotifications = useMemo(
    () => filterByPriorityAndUnread(notifications),
    [notifications, filterByPriorityAndUnread]
  );

  const groupedNotifications = useMemo(
    () => groupNotificationsForBell(filteredNotifications),
    [filteredNotifications]
  );

  const groupedModalNotifications = useMemo(() => {
    const raw = allData?.notifications || [];
    return groupNotificationsForBell(filterByPriorityAndUnread(raw));
  }, [allData?.notifications, filterByPriorityAndUnread]);

  const renderNotificationItem = (notification: GroupedNotification) => {
    const tier = getSemanticTier(notification);
    const title =
      notification._groupCount && notification._groupCount > 1 && !String(notification.title).includes('финансовых')
        ? `${notification._groupCount} новых событий: ${getDisplayTitle(notification)}`
        : getDisplayTitle(notification);
    const subtitle = getDisplaySubtitle(notification);
    const badges = getContextBadges(notification);
    const accent = getTierAccentColor(tier);

    return (
      <List.Item
        key={notification.id}
        className={getUnreadItemClassName(notification.isRead)}
        style={{
          borderLeft: `4px solid ${accent}`,
          cursor: canOpenNotification(notification) ? 'pointer' : 'default'
        }}
        onClick={() => {
          if (canOpenNotification(notification)) handleNotificationOpen(notification);
        }}
        actions={[
          canOpenNotification(notification) && (
            <Button
              key="open"
              type="text"
              size="small"
              icon={<LinkOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleNotificationOpen(notification);
              }}
              title="Открыть"
            />
          ),
          !notification.isRead && (
            <Button
              key="read"
              type="text"
              size="small"
              icon={<CheckOutlined />}
              onClick={(e) => handleMarkAsRead(notification.id, e)}
              title="Прочитано"
            />
          ),
          <Button
            key="dismiss"
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            onClick={(e) => handleDeleteNotification(notification.id, e)}
            title="Скрыть"
          />
        ].filter(Boolean)}
      >
        <List.Item.Meta
          avatar={
            <span className="notification-bell-avatar">
              <NotificationSemanticIcon notification={notification} />
            </span>
          }
          title={
            <div className="notification-bell-title-row">
              <Text
                strong={!notification.isRead}
                ellipsis
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 14,
                  color: notification.isRead ? 'var(--text-secondary)' : 'var(--text)'
                }}
              >
                {title}
              </Text>
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 8, flexShrink: 0, whiteSpace: 'nowrap' }}>
                {formatRelativeTimeRu(notification.createdAt)}
              </Text>
            </div>
          }
          description={
            <div>
              {badges.length > 0 && (
                <div>
                  {badges.map((b) => (
                    <Tag key={b} className="notification-context-badge" style={{ margin: 0, fontSize: 10 }}>
                      {b}
                    </Tag>
                  ))}
                </div>
              )}
              <Text
                type="secondary"
                ellipsis
                style={{
                  fontSize: 13,
                  lineHeight: 1.45,
                  display: 'block',
                  color: 'var(--text-secondary)',
                  wordBreak: 'break-word'
                }}
              >
                {subtitle}
              </Text>
            </div>
          }
        />
      </List.Item>
    );
  };

  const content = (
    <div className="notification-bell-panel">
      <div className="notification-bell-header">
        <div className="notification-bell-header-row">
          <Text strong style={{ color: 'var(--text)' }}>
            Уведомления
          </Text>
          <div className="notification-bell-header-actions">
            {isFetchingNotifications && <Spin size="small" />}
            <Button type="text" size="small" onClick={() => refetch()} icon={<ReloadOutlined />} title="Обновить" />
            {unreadCount > 0 && (
              <Button type="link" size="small" onClick={handleMarkAllAsRead} loading={loading} icon={<CheckOutlined />}>
                Прочитать все
              </Button>
            )}
          </div>
        </div>
        <div className="notification-bell-filters">
          <Button size="small" type={priorityFilter === 'all' ? 'primary' : 'default'} onClick={() => setPriorityFilter('all')}>
            Все
          </Button>
          <Button
            size="small"
            type={priorityFilter === 'important' ? 'primary' : 'default'}
            onClick={() => setPriorityFilter('important')}
          >
            Важные
          </Button>
          <Button size="small" type={unreadOnlyFilter ? 'primary' : 'default'} onClick={() => setUnreadOnlyFilter((v) => !v)}>
            Непрочитанные
          </Button>
        </div>
      </div>

      {isLoadingNotifications && !notificationsData ? (
        <div><Spin /></div>
      ) : groupedNotifications.length === 0 ? (
        <div>
          <Text type="secondary">Нет уведомлений</Text>
        </div>
      ) : (
        <List size="small" dataSource={groupedNotifications} renderItem={(n) => renderNotificationItem(n)} />
      )}

      <div className="notification-bell-footer">
        <Button type="link" onClick={() => { setLimitPopover((prev) => prev + 10); refetch(); }}>
          Показать еще
        </Button>
        <Button
          type="link"
          onClick={() => {
            setVisibleAll(true);
            setVisible(false);
          }}
        >
          Показать все
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        title={null}
        trigger="click"
        open={visible}
        onOpenChange={handleVisibleChange}
        placement="bottomRight"
        overlayClassName="notification-popover"
      >
        <Badge count={unreadCount} size="small" overflowCount={99}>
          <Button shape="circle" icon={<BellOutlined style={{ fontSize: 18, color: 'var(--text)' }} />} className="notification-bell-trigger" />
        </Badge>
      </Popover>

      <Modal
        title="Все уведомления"
        open={visibleAll}
        onCancel={() => setVisibleAll(false)}
        footer={null}
        width="min(720px, calc(100vw - 32px))"
        className="notification-all-modal"
        styles={{ body: { backgroundColor: 'var(--card)', padding: 0 } }}
      >
        <div>
          <Space wrap style={{ marginBottom: 12 }}>
            <Button size="small" type={priorityFilter === 'all' ? 'primary' : 'default'} onClick={() => setPriorityFilter('all')}>
              Все
            </Button>
            <Button
              size="small"
              type={priorityFilter === 'important' ? 'primary' : 'default'}
              onClick={() => setPriorityFilter('important')}
            >
              Важные
            </Button>
            <Button size="small" type={unreadOnlyFilter ? 'primary' : 'default'} onClick={() => setUnreadOnlyFilter((v) => !v)}>
              Непрочитанные
            </Button>
          </Space>
          {isLoadingAll ? (
            <div><Spin /></div>
          ) : (
            <>
              <List dataSource={groupedModalNotifications} renderItem={(n) => renderNotificationItem(n)} />
              <div className="notification-bell-footer">
                <Space>
                  <Button onClick={() => { setPageAll(1); setLimitAll(10); refetchAll(); }}>10</Button>
                  <Button onClick={() => { setPageAll(1); setLimitAll(20); refetchAll(); }}>20</Button>
                  <Button onClick={() => { setPageAll(1); setLimitAll(50); refetchAll(); }}>50</Button>
                </Space>
                <Pagination
                  current={pageAll}
                  pageSize={limitAll}
                  total={allData?.pagination?.total || 0}
                  onChange={(p, ps) => {
                    setPageAll(p);
                    setLimitAll(ps);
                    refetchAll();
                  }}
                  showSizeChanger={false}
                  size="small"
                />
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};


export default NotificationBell;
